import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FC, useMemo } from "react";

import { SectionCard } from "~/src/components/common/SectionCard";
import { chakraFactory } from "~/src/lib/chakra-ui";

const StyledSectionCard = chakraFactory(SectionCard, {
  baseStyle: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    overflow: "hidden",
  },
});

const StyledCodeWrapper = chakraFactory(Box, {
  baseStyle: {
    background: "#FAFAFA",
    border: "1px solid #DADADA",
    borderRadius: "2px",
    padding: "12px",
    fontSize: "10px",
    letterSpacing: "0.02em",
    color: "#000000",
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    fontFamily: "monospace",
    flex: 1,
    overflowX: "auto",
    overflowY: "auto",
    minHeight: "200px",
    minWidth: 0,
    height: "100%",
  },
});

/** Linked (配信条件連動) module shown on Tab 2 of コンパイル済みプレビュー. */
export type CompiledPreviewLinkedModule = {
  id: string;
  name: string;
  /** Effective code (draft when present, else published). */
  content: string;
  hasDraft: boolean;
  hasPendingToggle?: boolean;
};

type Props = {
  mergedTagPreview: string | null;
  draftMergedTagPreview: string | null;
  hasPendingChanges: boolean;
  /** True when any module has saved code draft (draftContent). */
  hasPendingCodeChanges?: boolean;
  /** True when any module has a reserved on/off (draftEnabled). */
  hasPendingToggleChanges?: boolean;
  enabledModuleCount: number;
  totalModuleCount: number;
  gatedModuleCount?: number;
  /**
   * Enabled / pending 配信条件連動 modules for Tab 2.
   * When provided (including `[]`), the preview uses SDK + linked tabs.
   */
  linkedModules?: CompiledPreviewLinkedModule[];
  onCancelChanges?: () => void;
  onPublishChanges?: () => void;
  isPublishDisabled?: boolean;
};

export const CompiledPreview: FC<Props> = ({
  mergedTagPreview,
  draftMergedTagPreview,
  hasPendingChanges,
  hasPendingCodeChanges = false,
  hasPendingToggleChanges = false,
  enabledModuleCount: _enabledModuleCount,
  totalModuleCount: _totalModuleCount,
  gatedModuleCount = 0,
  linkedModules,
  onCancelChanges,
  onPublishChanges,
  isPublishDisabled = false,
}) => {
  const currentPreview = hasPendingChanges
    ? draftMergedTagPreview
    : mergedTagPreview;
  const hasContent = currentPreview && currentPreview.trim().length > 0;
  const showLinkedTab = linkedModules != null;
  const linkedCount = linkedModules?.length ?? 0;

  const linkedDefaultIndex = useMemo(() => {
    if (!linkedModules || linkedModules.length === 0) return [];
    const firstPending = linkedModules.findIndex(
      (m) => m.hasDraft || m.hasPendingToggle
    );
    return [firstPending >= 0 ? firstPending : 0];
  }, [linkedModules]);

  const sdkPreview = (
    <StyledCodeWrapper>
      {hasContent ? currentPreview : "// 有効なモジュールがありません"}
    </StyledCodeWrapper>
  );

  const linkedPreview =
    linkedModules == null || linkedModules.length === 0 ? (
      <Box
        flex={1}
        minH="200px"
        borderWidth="1px"
        borderStyle="solid"
        borderColor="#DADADA"
        borderRadius="2px"
        bg="#FAFAFA"
        px={3}
        py={4}
      >
        <Text fontSize="xs" color="gray.500">
          配信条件連動モジュールはありません。
        </Text>
      </Box>
    ) : (
      <Box flex={1} minH={0} overflowY="auto" minHeight="200px">
        <Text fontSize="xs" color="gray.500" mb={2}>
          CDNマージから除外されます。下書きがあるモジュールは下書きコードを表示します。
        </Text>
        <Accordion
          allowMultiple
          defaultIndex={linkedDefaultIndex}
          key={linkedModules.map((m) => m.id).join("|")}
        >
          {linkedModules.map((module) => (
            <AccordionItem
              key={module.id}
              borderWidth="1px"
              borderStyle="solid"
              borderColor="#DADADA"
              borderRadius="2px"
              mb={2}
              bg="white"
              overflow="hidden"
              _last={{ mb: 0 }}
            >
              <AccordionButton
                px={3}
                py={2}
                _hover={{ bg: "gray.50" }}
                _focus={{ boxShadow: "none" }}
              >
                <HStack spacing={2} flex={1} textAlign="left" minW={0}>
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="gray.800"
                    wordBreak="break-word"
                  >
                    {module.name}
                  </Text>
                  <Badge colorScheme="purple" size="sm" flexShrink={0}>
                    配信条件連動
                  </Badge>
                  {module.hasDraft && (
                    <Badge colorScheme="orange" size="sm" flexShrink={0}>
                      下書き
                    </Badge>
                  )}
                  {module.hasPendingToggle && (
                    <Badge colorScheme="blue" size="sm" flexShrink={0}>
                      切替予定
                    </Badge>
                  )}
                </HStack>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel
                px={3}
                pb={3}
                pt={2}
                borderTopWidth="1px"
                borderColor="#DADADA"
              >
                <StyledCodeWrapper
                  minHeight="120px"
                  height="auto"
                  maxHeight="280px"
                >
                  {module.content.trim().length > 0
                    ? module.content
                    : "// (空)"}
                </StyledCodeWrapper>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </Box>
    );

  return (
    <StyledSectionCard>
      <VStack spacing={3} align="stretch" height="100%" minW={0}>
        <Flex
          flexWrap="wrap"
          align="center"
          gap={2}
          rowGap={3}
          sx={{
            "@media (max-width: 1160px)": {
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 3,
            },
          }}
        >
          <HStack
            spacing={2}
            align="center"
            flexShrink={0}
            sx={{
              "@media (max-width: 1160px)": {
                width: "100%",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 2,
              },
            }}
          >
            <Text
              fontSize="lg"
              fontWeight="semibold"
              color="gray.700"
              flexShrink={0}
            >
              コンパイル済みプレビュー
            </Text>
            {(hasPendingCodeChanges || hasPendingToggleChanges) && (
              <HStack spacing={2} flexWrap="wrap">
                {hasPendingCodeChanges && (
                  <Badge colorScheme="orange" size="sm" flexShrink={0}>
                    コード下書き
                  </Badge>
                )}
                {hasPendingToggleChanges && (
                  <Badge colorScheme="blue" size="sm" flexShrink={0}>
                    有効/無効の予約
                  </Badge>
                )}
              </HStack>
            )}
          </HStack>

          <HStack
            spacing={2}
            flexShrink={0}
            ml="auto"
            sx={{
              "@media (max-width: 1160px)": {
                width: "100%",
                marginLeft: 0,
                justifyContent: "flex-end",
              },
            }}
          >
            {onCancelChanges && (
              <Button
                size="sm"
                variant="outline"
                colorScheme="gray"
                onClick={onCancelChanges}
                isDisabled={!hasPendingChanges}
              >
                変更を取り消す
              </Button>
            )}
            {onPublishChanges && (
              <Button
                size="sm"
                colorScheme="blue"
                onClick={onPublishChanges}
                isDisabled={!hasPendingChanges || isPublishDisabled}
              >
                変更を公開
              </Button>
            )}
          </HStack>
        </Flex>

        {gatedModuleCount > 0 && (
          <Text fontSize="xs" color="gray.500">
            配信条件連動モジュール（{gatedModuleCount}
            件）はCDNマージから除外され、訪問時にリンク先の配信条件を満たした場合のみ実行されます。
          </Text>
        )}

        {showLinkedTab ? (
          <Tabs
            display="flex"
            flexDirection="column"
            flex={1}
            minH={0}
            overflow="hidden"
            isLazy
          >
            <TabList flexShrink={0}>
              <Tab fontSize="sm">SDK / コンパイル</Tab>
              <Tab fontSize="sm">配信条件連動 ({linkedCount})</Tab>
            </TabList>
            <TabPanels
              display="flex"
              flexDirection="column"
              flex={1}
              minH={0}
              overflow="hidden"
              pt={3}
            >
              <TabPanel
                p={0}
                display="flex"
                flexDirection="column"
                flex={1}
                minH={0}
                overflow="hidden"
                h="100%"
              >
                {sdkPreview}
              </TabPanel>
              <TabPanel
                p={0}
                display="flex"
                flexDirection="column"
                flex={1}
                minH={0}
                overflow="hidden"
                h="100%"
              >
                {linkedPreview}
              </TabPanel>
            </TabPanels>
          </Tabs>
        ) : (
          sdkPreview
        )}
      </VStack>
    </StyledSectionCard>
  );
};
